import type { EventResult } from '~/types/interfaces'
import type { CreditLine } from './useWorkApi'
import { useApiBase } from './useApiBase'

export interface CheckCodeResponse {
  status: string
  score?: number
  comment?: string
}

export interface ListProjectItem {
  name: string
  type: string
  projectId: string
  readonly: boolean
  description: string
  createdAt: string
  updatedAt?: string
}

export interface ListProjectsResponse {
  projects: ListProjectItem[]
}

export interface ProjectFile {
  name: string
  alias?: string
  readonly: boolean
}

export interface GetProjectResponse {
  projectId: string
  type: string
  title: string
  files: ProjectFile[]
}

export interface GetProjectFileResponse {
  code: string
}

export interface UpdateProjectFileResponse {
  savedBytes: number
}

export interface CreateProjectFileResponse {
  name: string
  alias?: string
}

export interface DeleteProjectFileResponse {
  name: string
}

export interface RenameProjectFileResponse {
  name: string
  alias?: string
}

export interface DuplicateProjectFileResponse {
  name: string
  alias?: string
}

export interface ProjectFileLineageResponse {
  name: string
  inheritedAuthorCount: number
  entries: Array<Record<string, unknown>>
}

// #743: 隠しリネージュビューア (魔導書の系譜)。
export interface LineageAncestor {
  projectId: string
  title: string
  authorName: string
  authorUserId: string
  copiedAt: string
  copiedFileNames: string[]
  parentAccessible: boolean
}

// バックエンドは ApiResponse.serialize_body の exclude_none=True により null 値の
// フィールドを wire JSON から省略する（null では送らない）。そのため各イベント種別で
// 該当しないフィールドは「存在しない」= optional として扱う（`=== null` 比較は無意味）。
export interface LineageFileEvent {
  type: 'created' | 'copied' | 'crystallized' | 'renamed' | 'duplicated'
  at?: string
  fromProjectId?: string
  fromProjectTitle?: string
  fromAuthorName?: string
  fromFileName?: string
  oldName?: string
  newName?: string
  parentAccessible?: boolean
}

export interface LineageFile {
  name: string
  events: LineageFileEvent[]
}

export interface LineageViewResponse {
  projectId: string
  title: string
  type: string
  // #750: 「作り手」projection（authored/edited）。.pyxres の per-bank 帰属も含む。
  credits: CreditLine[]
  ancestors: LineageAncestor[]
  files: LineageFile[]
}

// Re-uses CreateEventTriggerResponse for createAdvice
export interface CreateEventTriggerResponse {
  results: EventResult[]
  internalPrompt?: string
}

export interface AdvisorSubOption {
  optionId: string
  label: string
}

export interface AdvisorMenuOption {
  optionId: string
  label: string
  requiresCode: boolean
  requiresSelection?: boolean
  subOptions?: AdvisorSubOption[]
}

// センパイの自発ヒント (#745)。
// クライアントが観測した「迷い」。コードだけでは分からないことだけを送る。
export interface ProactiveHintSignals {
  /** 最後の編集からの経過秒数 */
  idleSeconds: number
  /** 構文エラーが解消されないまま続いている秒数（0 = エラー無し） */
  errorSeconds: number
  addedChars: number
  deletedChars: number
  /** 同じあたりを打っては消しした往復回数 */
  retypeLoops: number
  /** 直近の窓で undo を叩いた回数 */
  undoCount: number
  /** この魔導書でまだ一度も実行していない */
  neverRan: boolean
  /** 最後の実行からの追加行数 */
  linesSinceRun: number
  /** 何も直さずに実行し直した連続回数 */
  runsWithoutEdit: number
  /** 直前の実行で Python が止まった理由 */
  runtimeError?: { errorType: string, name: string, message: string }
  /** 長く離れていて戻ってきた直後 */
  returnedAfterAway: boolean
  /** `_` をキーボード上で探し回った回数 (#125) */
  underscoreHunts: number
  /** `_` のキーの場所をこれまでに何回教えたか（0=まだ / 1=JIS / 2=US まで） (#125) */
  underscoreHintsGiven: number
  suppressedHintIds: string[]
}

export interface ProactiveHint {
  hintId: string
  message: string
  advisorId: string
  categoryId: string
}

export interface ProactiveHintResponse {
  // 出すものが無いときはキーごと落ちてくる（exclude_none）。
  hint?: ProactiveHint
  /** いま構文エラーがあるか。クライアントは継続時間の起点をこれで持つ。 */
  hasSyntaxError?: boolean
}

// 羽根筆の編集提案 (#937/#938)。POST /api/v1/projects/{project}/quill
export type QuillDecision = 'implement' | 'clarify' | 'redirect' | 'refuse'

export interface QuillEdit {
  search: string
  replace: string
}

export interface QuillClarification {
  /** 羽根筆の迷い（聞き返しの一言） */
  question: string
  /** 選んだ答え */
  answer: string
}

export interface QuillResult {
  status: 'ok' | 'failed'
  decision?: QuillDecision | null
  /** 吹き出しの一言（利用者の考えごと。50 字まで） */
  message: string
  /** ⓘ で開く細かい解説 */
  detail?: string
  options?: string[]
  edits?: QuillEdit[]
  autoRedirected?: boolean
  /** 写経で教える型（`<skill>.<key>`。#965）。あれば、書いたものは入れずに利用者が写す見本 */
  teaches?: string[]
  /** 写経で教える型の名前（例: 「回数の繰り返し（for と range）」。#979）。写経の画面に「〇〇をひらめいた」と出す */
  teachLabel?: string
  /** 次の一段（#976）。大きな願いを段に分けて書いたとき、目標までの残りを 3 択で出す。選ぶとその段を新しい依頼として送る */
  nextSteps?: string[]
}

export interface CreateQuillResponse {
  result: QuillResult
  /** 適用したときに取る MP（書いたときだけ） */
  mpCost: number
  /** 適用のチケット（書いたときだけ）。適用したら applyQuill に渡す（MP はそのとき取られる） */
  ticket?: string | null
  /** 書く直前の Revision（「この時点に戻す」の戻り先） */
  revisionId?: string | null
}

/** 魔導書ごとのやりとりの履歴の 1 件（#438）。羽根筆の依頼か、キャラクターへの相談 */
export interface HistoryEntry {
  entryId: string
  kind: 'quill' | 'advice'
  createdAt: string
  /** 思い描いたこと（羽根筆）/ 質問（相談） */
  request: string
  /** 羽根筆の一言 / 相談の答え（Markdown） */
  message: string
  fileName?: string | null
  decision?: 'implement' | 'clarify' | 'redirect' | 'refuse' | null
  status?: 'ok' | 'failed' | null
  clarificationQuestion?: string | null
  clarificationAnswer?: string | null
  applied?: boolean
  advisorType?: string | null
}

export interface AdvisorMenuInfo {
  advisorId: string
  name: string
  mpCost: number
  advisorMode: string
  supportedProjectTypes: string[]
  menu?: AdvisorMenuOption[]
}

export function useProjectApi() {
  const { apiBase, authenticatedFetch } = useApiBase()

  const listProjects = async (
    usecase?: 'vault' | 'remix_source' | 'decoded' | 'all_crystals',
  ) => {
    return authenticatedFetch<ListProjectsResponse>(
      `${apiBase}/api/v1/projects`,
      {
        method: 'GET',
        query: { usecase },
      },
    )
  }

  const getProject = async (projectId: string) => {
    return authenticatedFetch<GetProjectResponse>(
      `${apiBase}/api/v1/projects/${projectId}`,
    )
  }

  const getProjectFile = async (projectId: string, name: string) => {
    return authenticatedFetch<GetProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}`,
    )
  }

  const updateProjectFile = async (projectId: string, name: string, code: string) => {
    return authenticatedFetch<UpdateProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}`,
      {
        method: 'PUT',
        body: JSON.stringify({ code }),
      },
    )
  }

  const createAdvice = async (
    projectId: string,
    advisorId: string,
    fileName: string,
    options?: { userPrompt?: string, categoryId?: string, detailId?: string, selectionStartLine?: number, selectionEndLine?: number },
  ) => {
    return authenticatedFetch<CreateEventTriggerResponse>(
      `${apiBase}/api/v1/projects/${projectId}/advisor`,
      {
        method: 'POST',
        body: JSON.stringify({
          advisorId,
          fileName,
          ...options,
        }),
      },
    )
  }

  // 羽根筆の編集提案 (#938)。ファイルは書かない（置き換えを返すだけ）。保存済みのコードを読むので、
  // 呼ぶ前に保存を流しておくこと（useCode の flushCodeAsync）
  const createQuill = async (
    projectId: string,
    fileName: string,
    request: string,
    clarification?: QuillClarification,
    /** 段を重ねている途中の、最初に思い描いたこと（#976） */
    goal?: string,
  ) => {
    return authenticatedFetch<CreateQuillResponse>(
      `${apiBase}/api/v1/projects/${projectId}/quill`,
      {
        method: 'POST',
        body: JSON.stringify({ fileName, request, clarification, goal: goal || undefined }),
      },
    )
  }

  // 羽根筆の見本を写し終えた (#965)。教えると申告した型を覚える（無料）
  const copiedQuill = async (projectId: string, ticket: string) => {
    return authenticatedFetch<{ learned: string[] }>(
      `${apiBase}/api/v1/projects/${projectId}/quill/copied`,
      {
        method: 'POST',
        body: JSON.stringify({ ticket }),
      },
    )
  }

  // 羽根筆が書いたものを適用した (#939)。MP はここで取られる（2026-10-03 doi: 適用したときに取る）
  const applyQuill = async (projectId: string, ticket: string) => {
    return authenticatedFetch<{ mpCost: number }>(
      `${apiBase}/api/v1/projects/${projectId}/quill/apply`,
      {
        method: 'POST',
        body: JSON.stringify({ ticket }),
      },
    )
  }

  // 魔導書ごとのやりとりの履歴（羽根筆 + 相談。新しい順に 20 件。#438）
  // before を渡すと、その時刻より前だけ（パネルの列をさかのぼる。#954）
  const listHistory = async (projectId: string, before?: string) => {
    return authenticatedFetch<{ entries: HistoryEntry[] }>(
      `${apiBase}/api/v1/projects/${projectId}/history`,
      { method: 'GET', query: before ? { before } : undefined },
    )
  }

  // ユーザー評価を送信する (#654)。
  // target は ratingTarget をそのまま受ける (targetType + targetId)。
  // rating は "helpful" / "difficult" / "unhelpful" のいずれか。
  // 将来 target_type が増えても URL は同じ。
  const submitUserRating = async (
    targetType: 'advisor_response',
    targetId: string,
    rating: 'helpful' | 'difficult' | 'unhelpful',
  ) => {
    return authenticatedFetch<Record<string, never>>(
      `${apiBase}/api/v1/ratings`,
      {
        method: 'POST',
        body: JSON.stringify({ targetType, targetId, rating }),
      },
    )
  }

  const checkCurrentCode = async (sceneId: string) => {
    return authenticatedFetch<CheckCodeResponse>(
      `${apiBase}/api/v1/mycodes/${sceneId}/check`,
      { method: 'GET' },
    )
  }

  const listAdvisors = async (projectId?: string, fileName?: string) => {
    const query: Record<string, string> = {}
    if (projectId) query.project = projectId
    if (fileName) query.fileName = fileName
    return authenticatedFetch<{ advisors: AdvisorMenuInfo[] }>(
      `${apiBase}/api/v1/advisors`,
      { query },
    )
  }

  // センパイの自発ヒント (#745)。LLM も MP も使わない rule-based の軽量判定なので、
  // タイピングが止まったタイミングで気軽に叩いてよい（頻度制御は useProactiveHint 側）。
  const getProactiveHint = async (
    projectId: string,
    fileName: string,
    signals: ProactiveHintSignals,
  ) => {
    return authenticatedFetch<ProactiveHintResponse>(
      `${apiBase}/api/v1/projects/${projectId}/advisor/hint`,
      {
        method: 'POST',
        body: JSON.stringify({ fileName, signals }),
      },
    )
  }

  const renameProject = async (projectId: string, title: string) => {
    return authenticatedFetch<{ projectId: string, title: string }>(
      `${apiBase}/api/v1/projects/${projectId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      },
    )
  }

  const deleteProject = async (projectId: string) => {
    return authenticatedFetch<{ projectId: string }>(
      `${apiBase}/api/v1/projects/${projectId}`,
      {
        method: 'DELETE',
      },
    )
  }

  // --- File-unit operations (#601) ---

  const createProjectFile = async (
    projectId: string,
    name: string,
    content?: string,
  ) => {
    return authenticatedFetch<CreateProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files`,
      {
        method: 'POST',
        body: JSON.stringify({ name, content }),
      },
    )
  }

  const deleteProjectFile = async (projectId: string, name: string) => {
    return authenticatedFetch<DeleteProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}`,
      { method: 'DELETE' },
    )
  }

  const renameProjectFile = async (
    projectId: string,
    name: string,
    newName: string,
  ) => {
    return authenticatedFetch<RenameProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ newName }),
      },
    )
  }

  const duplicateProjectFile = async (projectId: string, name: string) => {
    return authenticatedFetch<DuplicateProjectFileResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}/duplicate`,
      { method: 'POST' },
    )
  }

  const getProjectFileLineage = async (projectId: string, name: string) => {
    return authenticatedFetch<ProjectFileLineageResponse>(
      `${apiBase}/api/v1/projects/${projectId}/files/${name}/lineage`,
      { method: 'GET' },
    )
  }

  // #743: 隠しリネージュビューア。プロジェクト単位の系譜（祖先 + 頁ごとの由来イベント）を取得する。
  const getProjectLineageView = async (projectId: string) => {
    return authenticatedFetch<LineageViewResponse>(
      `${apiBase}/api/v1/projects/${projectId}/lineage-view`,
      { method: 'GET' },
    )
  }

  return {
    listProjects,
    getProject,
    getProjectFile,
    updateProjectFile,
    createAdvice,
    createQuill,
    applyQuill,
    copiedQuill,
    listHistory,
    submitUserRating,
    checkCurrentCode,
    listAdvisors,
    getProactiveHint,
    renameProject,
    deleteProject,
    createProjectFile,
    deleteProjectFile,
    renameProjectFile,
    duplicateProjectFile,
    getProjectFileLineage,
    getProjectLineageView,
  }
}
